import { motion } from 'framer-motion';
import { GraduationCap, Wrench, MapPin, Clock, Users, CheckCircle } from 'lucide-react';
import { useProposalData } from '../../context';
import { Section, SectionHeader } from '../layout';
import { formatCurrency } from '../../utils';

export function ServicesSection() {
  const proposal = useProposalData();

  if (!proposal.sections.showServices) {
    return null;
  }

  const hasTraining = proposal.services.training && proposal.services.training.length > 0;
  const hasPMContracts = proposal.equipment.some(eq => eq.services?.pmContract);
  const hasGPS = proposal.equipment.some(eq => eq.services?.gps?.added);

  if (!hasTraining && !hasPMContracts && !hasGPS) {
    return null;
  }

  // Calculate PM contract totals
  const pmTotal = proposal.equipment.reduce((sum, eq) => {
    if (eq.services?.pmContract) {
      return sum + (eq.services.pmContract.price * eq.quantity);
    }
    return sum;
  }, 0);

  // Calculate GPS totals
  const gpsTotal = proposal.equipment.reduce((sum, eq) => {
    if (eq.services?.gps?.added) {
      return sum + (eq.services.gps.price * eq.quantity);
    }
    return sum;
  }, 0);

  return (
    <Section id="services" dark pattern="grid">
      <SectionHeader
        title="SERVICES & SUPPORT"
        subtitle="Value-Added"
        dark
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Training Card */}
        {hasTraining && (
          <motion.div
            className="bg-white rounded-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-hul-orange to-amber-500 p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-white tracking-wide">
                    TRAINING
                  </h3>
                  <p className="text-white/80 text-sm">OSHA Compliant Certification</p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-4">
                {proposal.services.training!.map((training, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                  >
                    <div>
                      <div className="font-medium text-hul-navy">{training.type}</div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Users className="w-4 h-4" />
                        <span>{training.quantity} operators</span>
                        <span className="text-gray-300">·</span>
                        <span>{formatCurrency(training.pricePerOperator)}/person</span>
                      </div>
                    </div>
                    <div className="font-mono font-bold text-hul-navy">
                      {formatCurrency(training.total)}
                    </div>
                  </div>
                ))}
              </div>

              {/* Training Benefits */}
              <div className="mt-6 p-4 bg-amber-50 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Includes:</h4>
                <ul className="space-y-1 text-sm text-amber-700">
                  {[
                    'Online or Classroom instruction',
                    'Hands-on evaluation',
                    'Certification test',
                    'Operator certification cards',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Total */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="font-heading text-gray-600">Training Total</span>
                <span className="font-mono text-2xl font-bold text-hul-red">
                  {formatCurrency(proposal.services.totalServices)}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* PM Contracts Card */}
        {hasPMContracts && (
          <motion.div
            className="bg-white rounded-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-hul-navy to-hul-navy-dark p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Wrench className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-white tracking-wide">
                    MAINTENANCE
                  </h3>
                  <p className="text-white/80 text-sm">Planned Maintenance Programs</p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-4">
                {proposal.equipment
                  .filter(eq => eq.services?.pmContract)
                  .map((eq, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <div className="font-medium text-hul-navy">
                          {eq.manufacturer} {eq.model}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <Clock className="w-4 h-4" />
                          <span>PM</span>
                          <span className="text-gray-300">·</span>
                          <span>Every {eq.services!.pmContract!.frequency}{!String(eq.services!.pmContract!.frequency).toLowerCase().includes('day') ? ' days' : ''}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-hul-navy">
                          {formatCurrency(eq.services!.pmContract!.price * eq.quantity)}
                        </div>
                        <div className="text-xs text-gray-500">/service</div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* PM Benefits */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Program Includes:</h4>
                <ul className="space-y-1 text-sm text-blue-700">
                  {[
                    'Scheduled inspections',
                    'All labor & travel included',
                    'Parts billed as needed',
                    'Priority emergency service',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Total */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="font-heading text-gray-600">PM Services Total</span>
                <span className="font-mono text-2xl font-bold text-hul-red">
                  {formatCurrency(pmTotal)}<span className="text-sm font-normal">/service</span>
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* GPS Tracking Card */}
        {hasGPS && (
          <motion.div
            className="bg-white rounded-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            {/* Card Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-display text-2xl text-white tracking-wide">
                    FLEET TELEMATICS
                  </h3>
                  <p className="text-white/80 text-sm">GPS Tracking System</p>
                </div>
              </div>
            </div>

            {/* Card Body */}
            <div className="p-6">
              <div className="space-y-4">
                {proposal.equipment
                  .filter(eq => eq.services?.gps?.added)
                  .map((eq, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0"
                    >
                      <div>
                        <div className="font-medium text-hul-navy">
                          {eq.manufacturer} {eq.model}
                        </div>
                        <div className="text-sm text-gray-500">
                          {eq.quantity} unit{eq.quantity > 1 ? 's' : ''}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono font-bold text-hul-navy">
                          {formatCurrency(eq.services!.gps!.price * eq.quantity)}
                        </div>
                        <div className="text-xs text-gray-500">one-time</div>
                      </div>
                    </div>
                  ))}
              </div>

              {/* GPS Benefits */}
              <div className="mt-6 p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-2">Features:</h4>
                <ul className="space-y-1 text-sm text-purple-700">
                  {[
                    'Location tracking',
                    'Geofence alerts',
                    'Usage reporting',
                    'Maintenance reminders',
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Total */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex justify-between items-center">
                <span className="font-heading text-gray-600">GPS Total</span>
                <span className="font-mono text-2xl font-bold text-hul-red">
                  {formatCurrency(gpsTotal)}
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </Section>
  );
}
